import { useCallback, useMemo } from 'react';
import { useInfiniteQuery } from 'react-query';
import { getScheduleData } from '@dispatch/shared/services';
import {
  SuperApiTask,
  SuperApiCalendar,
  SuperApiResource,
  TaskPeril,
  ScheduleFilters,
  EVENT_TYPE,
} from '@dispatch/shared/types';
import { differenceInMinutes } from 'date-fns';
import { z } from 'zod';

type Task = z.infer<typeof SuperApiTask>;
type Calendar = z.infer<typeof SuperApiCalendar>;
type Resource = z.infer<typeof SuperApiResource>;

// Extended task type with optional fields returned by API but not in schema
type TaskWithLocation = Task & {
  locationData?: {
    geoJson?: {
      properties?: {
        postalCode?: string;
      };
    };
  };
};

/**
 * Extended interval type with optional display properties
 * These may be present in API response but not defined in base zod schema
 */
interface IntervalWithDisplay {
  name?: string;
  cls?: string;
  iconCls?: string;
  startDate?: string | Date;
  endDate?: string | Date;
  isWorking?: boolean;
  recurrentStartDate?: string;
  recurrentEndDate?: string;
}

/**
 * Transform calendar data to include cls and iconCls properties for Bryntum
 * - Calendar-level iconCls: Used by Bryntum to render icon in non-working time blocks
 * - Calendar-level cls: Styling class for the calendar
 * - Interval-level cls: Used by resourceNonWorkingTime tooltip text
 */
function mapCalendars(calendars: Calendar[]): Calendar[] {
  return calendars.map((calendar) => {
    const hasNoActiveIntervals =
      calendar.intervals.length <= 0 ||
      (calendar.intervals.length === 1 &&
        calendar.intervals[0].name === 'Non working time');

    return {
      ...calendar,
      iconCls: hasNoActiveIntervals ? 'b-fa b-fa-calendar-xmark' : '',
      cls: 'scheduler2',
      intervals: calendar.intervals.map((interval) => {
        // Type assertion to access properties that may exist in API response
        const intervalExt = interval as IntervalWithDisplay;
        return {
          ...interval,
          // Preserve existing cls from API, or use name as fallback for tooltip text
          cls: intervalExt.cls || interval.name || '',
          // Preserve iconCls from API if present
          iconCls: intervalExt.iconCls || '',
        };
      }),
    };
  });
}

/**
 * Map API resources onto the Bryntum resource records.
 *
 * Resources otherwise pass through untouched; the one normalisation is
 * `claimOwnerCapacity`. The API omits the property entirely for a resource
 * with no Claim Owner capacity configured, and may also send an explicit
 * `null`. Both mean the same thing (AC 8), so they collapse to `undefined`
 * here and consumers need a single nullish check instead of two. Crucially it
 * is never zero-filled, so "not configured" stays distinguishable from a real
 * cap of 0.
 */
function mapResources(resources: Resource[]): Resource[] {
  return resources.map((resource) => ({
    ...resource,
    claimOwnerCapacity: resource.claimOwnerCapacity ?? undefined,
  }));
}

const taskColors = {
  green: '#88BBA1',
  grey: '#464647',
  blue: 'blue',
  orange: '#FF9900',
};

function mapEventName(event: Task): string {
  try {
    if (event.type === 'task') {
      return (
        [event?.insuredName?.trim(), event?.claimNumber?.trim()]
          .filter(Boolean)
          .join(' - ') || '-'
      );
    }
    return event.name;
  } catch {
    return '-';
  }
}

function mapColor(event: Task, perilData: { [code: string]: TaskPeril }) {
  const defaultPerilColor = taskColors.green;

  if (event?.methodOfInspection === 'VIRTUAL') {
    return taskColors.orange;
  }

  if (event?.peril !== undefined && perilData[event.peril] !== undefined) {
    return perilData[event.peril]?.color || defaultPerilColor;
  }

  if (event?.type === 'task') {
    return taskColors.blue;
  }

  return taskColors.grey;
}

function mapIcons(event: Task, perilData: { [code: string]: TaskPeril }) {
  if (event.type === 'CLAIM_OWNER') return '';
  if (
    event?.peril !== undefined &&
    perilData[event.peril] !== undefined &&
    perilData[event.peril].iconClassName !== undefined
  ) {
    return perilData[event.peril].iconClassName;
  }
  if (event?.peril) {
    return 'b-fa b-fa-house-crack';
  }
  return 'b-fa b-fa-calendar';
}

function mapEvents(
  events: TaskWithLocation[],
  perilData: { [code: string]: TaskPeril }
) {
  return (events || []).map((event: TaskWithLocation) => {
    const isTask = EVENT_TYPE.includes(event.type);

    // Virtual tasks must never render a drive-time travel buffer, regardless
    // of what the API serves. For virtual events the difference computation is
    // skipped and preamble/postamble resolve to 0, which maps to an empty
    // buffer below. Uses the same VIRTUAL check as mapColor for consistency.
    const isVirtual = event?.methodOfInspection === 'VIRTUAL';

    const preamble =
      !isVirtual && event?.driveStartTime && event.startDate
        ? differenceInMinutes(
            new Date(event.startDate),
            new Date(event.driveStartTime)
          )
        : 0;

    const postamble =
      !isVirtual && event?.homeArrivalTime && event.endDate
        ? differenceInMinutes(
            new Date(event.homeArrivalTime),
            new Date(event.endDate)
          )
        : 0;

    const mapped = {
      ...event,
      startDate: event.startDate ? new Date(event.startDate) : undefined,
      endDate: event.endDate ? new Date(event.endDate) : undefined,
      ignoreResourceCalendar: true,
      name: mapEventName(event),
      eventColor: mapColor(event, perilData),
      iconCls: mapIcons(event, perilData),
      targetQueue: event.targetQueue,
      isTask,
      draggable: event.isRescheduleAllowed === true,
      resizable: false,
      clickable: event.type !== 'no-location',
      source: 'scheduler',
      preamble: preamble > 0 ? `${preamble}min` : '',
      postamble: postamble > 0 ? `${postamble}min` : '',
      postalCode: event.locationData?.geoJson?.properties?.postalCode || '',
    };

    // Set a stable id so the SchedulerPage placeholder swap can match by
    // id when the refetched real event arrives:
    // - Tasks: id = taskReferenceId (matches the unassigned-grid extRefId
    //   stored in the placeholder).
    // - Blocked-time events: id = externalId (the UUID the editor-create
    //   path generated upfront and stamped on both the placeholder and
    //   the upsert payload). Falls back to event.id when externalId is
    //   absent so we don't break legacy responses.
    if (event.type?.toLowerCase() !== 'event' && event.taskReferenceId) {
      mapped.id = `${event.taskReferenceId}`;
    } else if (event.type?.toLowerCase() === 'event' && event.externalId) {
      mapped.id = event.externalId;
    }

    return mapped;
  });
}

const CACHE_TIME_MS = 30 * 1000;
const PAGE_SIZE = 50;

interface UseScheduleDataInfiniteOptions {
  filters: ScheduleFilters;
  perilData: { [code: string]: TaskPeril };
  enabled?: boolean;
  /**
   * Ask the API to attach Claim Owner capacity to each resource so the capacity
   * band has data. REQUIRED, deliberately: there is no default. The value comes
   * from the tenant flag AND the per-session toolbar toggle, so a default would
   * silently win over both and make the toggle inert. It is part of the query
   * key, so flipping it refetches rather than serving a cached page that has no
   * capacity on it.
   */
  includeClaimOwnerCapacity: boolean;
}

/**
 * Infinite scroll schedule data hook using continuation tokens
 * Fetches data in pages and accumulates results
 */
export function useScheduleDataInfinite({
  filters,
  perilData,
  enabled = true,
  includeClaimOwnerCapacity,
}: UseScheduleDataInfiniteOptions) {
  const filtersWithPageSize = useMemo(
    () => ({
      ...filters,
      pageSize: PAGE_SIZE,
    }),
    [filters]
  );

  // Serialize the full filters object so ANY filter change (add or clear) triggers a refetch.
  // JSON.stringify produces a stable string for the same values, avoiding spurious refetches.
  const filterKey = useMemo(() => JSON.stringify(filters), [filters]);

  const queryKey = [
    'schedule-data-infinite',
    filterKey,
    includeClaimOwnerCapacity,
  ];

  const query = useInfiniteQuery(
    queryKey,
    async ({ pageParam: continuationToken }) => {
      const response = await getScheduleData(
        filtersWithPageSize,
        continuationToken,
        includeClaimOwnerCapacity
      );
      return response;
    },
    {
      enabled: enabled && !!filters.scheduleStartDate,
      staleTime: 0, // Always refetch when query key changes
      cacheTime: CACHE_TIME_MS,
      refetchOnMount: 'always', // Force refetch when navigating back to scheduler
      refetchOnWindowFocus: false, // Prevent race condition with drag-create when returning focus
      getNextPageParam: (lastPage) => {
        // Return the continuation token if there's more data
        if (!lastPage.continuationToken) return undefined;
        try {
          const parsed = JSON.parse(lastPage.continuationToken);
          if (parsed?.ContinuationToken === null) return undefined;
          return lastPage.continuationToken;
        } catch {
          return lastPage.continuationToken;
        }
      },
    }
  );

  // Accumulate all data from all pages
  const accumulatedData = useMemo(() => {
    const pages = query.data?.pages || [];

    const allResources: Resource[] = [];
    const allEvents: TaskWithLocation[] = [];
    const allCalendars: Calendar[] = [];
    const resourceIds = new Set<string>();
    const eventIds = new Set<string>();
    const calendarIds = new Set<string>();

    pages.forEach((page) => {
      const data =
        page.data !== null && typeof page.data === 'object'
          ? (page.data as {
              resources?: Resource[];
              events?: TaskWithLocation[];
              calendars?: Calendar[];
            })
          : undefined;

      // Dedupe resources
      (data?.resources || []).forEach((r: Resource) => {
        if (!resourceIds.has(r.id)) {
          resourceIds.add(r.id);
          allResources.push(r);
        }
      });

      // Dedupe events
      (data?.events || []).forEach((e: TaskWithLocation) => {
        if (!eventIds.has(e.id)) {
          eventIds.add(e.id);
          allEvents.push(e);
        }
      });

      // Dedupe calendars
      (data?.calendars || []).forEach((c: Calendar) => {
        if (!calendarIds.has(c.id)) {
          calendarIds.add(c.id);
          allCalendars.push(c);
        }
      });
    });

    return {
      resources: mapResources(allResources),
      events: mapEvents(allEvents, perilData),
      calendars: mapCalendars(allCalendars),
    };
  }, [query.data?.pages, perilData]);

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  // Function to load more data
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    status: query.status,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    error: query.error,
    refetch: query.refetch,
    hasNextPage: query.hasNextPage ?? false,
    loadMore,
    data: accumulatedData,
  };
}
